import React from 'react';
import { Space, Button, Dropdown, Tooltip, Row, Modal, notification } from 'antd';
import { DownloadOutlined, ReloadOutlined, RobotOutlined, UploadOutlined } from '@ant-design/icons';
import { ShopifyShop } from '../../../store/shopifyShopSlice';

interface TableActionsProps {
    selectedRowKeys: React.Key[];
    selectedShopifyShop: ShopifyShop | null;
    setShopifyUploadApprovedContentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleAnalyzeShopifyProducts: () => Promise<{ scheduledCount?: number; quotaReached?: boolean; message?: string } | void>;
    handleReloadShopifyProducts: () => void;
}

const TableActions: React.FC<TableActionsProps> = ({
    selectedRowKeys,
    selectedShopifyShop,
    setShopifyUploadApprovedContentModalOpen,
    handleAnalyzeShopifyProducts,
    handleReloadShopifyProducts,
}) => {

    const handleMenuClick = (action: string, type: React.Key) => {
        if (action === 'download') {
            if (type === 'all') {
                console.log('Download All');
                // Implement download all logic
            } else if (type === 'selected') {
                console.log('Download Selected');
                // Implement download selected logic
            }
        }
    };

    const downloadMenu: {
        items: Array<{ key: React.Key; label: string; disabled?: boolean }>;
        onClick: ({ key }: { key: React.Key }) => void;
    } = {
        items: [
            { key: 'all', label: 'Download All' },
            { key: 'selected', label: `Download Selected (${selectedRowKeys.length})`, disabled: selectedRowKeys.length === 0 },
        ],
        onClick: ({ key }) => handleMenuClick('download', key),
    };


    const handleGenerateContent = () => {
        if (!selectedShopifyShop) return;

        const productCount = selectedShopifyShop.productCount ?? selectedShopifyShop.stats?.productCount ?? 0;
        Modal.confirm({
            title: 'Generate Content for All Products',
            content: `Are you sure you want to generate content for all ${productCount} products? This may take a while and consume your quota.`,
            okText: 'Yes, generate',
            cancelText: 'Cancel',
            onOk: async () => {
                const result = await handleAnalyzeShopifyProducts();
                const scheduledCount = result?.scheduledCount ?? 0;
                const quotaReached = result?.quotaReached ?? false;

                if (scheduledCount === 0) {
                    notification.warning({
                        message: 'No generations started',
                        description: result?.message ?? 'No new Shopify product generations were scheduled.',
                    });
                    return;
                }

                if (quotaReached) {
                    notification.warning({
                        message: 'Quota reached',
                        description: `Started ${scheduledCount} of ${productCount} Shopify product generations before the company quota was exhausted.`,
                    });
                    return;
                }

                notification.success({
                    message: 'Generation started',
                    description: `Started ${scheduledCount} Shopify product generations.`,
                });
            },
        });
    };

    return (
        <Row justify="end">
            <Space size="small">
                <Space.Compact>
                    <Tooltip title="Upload Approved Content to Shopify">
                        <Button
                            type="default"
                            icon={<UploadOutlined />}
                            onClick={() => setShopifyUploadApprovedContentModalOpen(true)}
                        />
                    </Tooltip>
                    <Tooltip title="Download">
                        <Dropdown menu={downloadMenu} trigger={['click']} aria-label="Download Options" >
                            <Button
                                disabled
                                type="default"
                                icon={<DownloadOutlined />}
                            />
                        </Dropdown>
                    </Tooltip>
                </Space.Compact>
                <Tooltip title="Generate Content">
                    <Button
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={handleGenerateContent}
                    />
                </Tooltip>
                <Tooltip title="Refresh">
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={handleReloadShopifyProducts}
                    />
                </Tooltip>
                {/* TODO: Make this selectable again */}
                {/* <Tooltip title="Analyze">
                                <Dropdown menu={analyzeMenu} trigger={['click']} aria-label="Analyze Options">
                                    <Button
                                        disabled
                                        type="primary"
                                        icon={<BarChartOutlined />}
                                    />
                                </Dropdown>
                            </Tooltip> */}
            </Space>
        </Row>
    );
};

export default TableActions;
